import {
  DatagridConfigurable,
  FunctionField,
  List,
  Pagination,
  SelectColumnsButton,
  TextField,
  TopToolbar,
} from "react-admin";
import BillingGuidelinesFilters from "./BillingGuidelinesFilters";
import { formatTimestamp } from "../utils/DateUtils";

const BillingGuidelines = () => {
  const MyListActions = () => (
    <TopToolbar>
      <SelectColumnsButton preferenceKey="billingGuidelines.list.columns" />
    </TopToolbar>
  );

  const formatPercentage = (value?: number) => {
    return value !== undefined && value !== null ? `${(value * 100)}%` : '';
  }

  const formatMonetaryValue = (value?: number) => {
    if (typeof(value) !== 'number') return value;
    return `$${value.toFixed(2)}`;
  }

  return (
    <>
      <List
        actions={<MyListActions />}
        resource="GetBillingGuidelines"
        perPage={10}
        pagination={<Pagination />}
        filters={<BillingGuidelinesFilters />}
        sx={{ '& .RaList-main': { p: 0 } }}
      >
        <DatagridConfigurable
          preferenceKey="billingGuidelines.list.columns"
          omit={[
            'recon_client_number_dst',
            'recon_narrow_fee',
            'recon_kinetiq_health_fee',
            'recon_turveris_fee',
            'recon_tpa_or_broker_fee',
            'recon_pgx_fee',
            'recon_total_fees',
            'recon_client_name_gp',
            'recon_utilizes_pos',
            'recon_step_therapy',
            'mpsa_client_number',
            'mpsa_client_name',
            'mpsa_contract_type',
            'mpsa_contract_effective_date',
            'mpsa_fully_executed',
            'mpsa_payment_terms',
            'mpsa_admin_fee_type',
            'mpsa_admin_fee_minimum',
            'mpsa_admin_fee_amount',
            'mpsa_narrow_fee',
            'mpsa_kinetiq_fee',
            'mpsa_turveris_fee',
            'mpsa_tpa_or_broker_fee',
            'mpsa_pgx_fee',
            'mpsa_total_fee',
            'mpsa_misc_amount',
            'mpsa_misc_fee_description',
            'mpsa_pa_clinical_fee',
            'mpsa_pa_specialty_fee',
            'mpsa_pa_standard_fee',
            'mpsa_pa_appeal1_fee',
            'mpsa_pa_appeal2_fee',
            'mpsa_total_access_pmpm_bundle',
            'mpsa_full_clinical_pmpm_bundle',
            'mpsa_limited_clinical_pmpm_bundle',
            'mpsa_true_advocate_percentage',
            'mpsa_true_advocate_max_amount',
            'mpsa_true_advocate_day_supply_cap',
            'mpsa_true_advocate_billing_method',
            'mpsa_true_advocate_pmpm',
            'mpsa_true_codes_percentage',
            'mpsa_true_codes_max_amount',
            'mpsa_true_codes_day_supply_cap',
            'mpsa_true_codes_billing_method',
            'mpsa_true_codes_pmpm',
            'mpsa_true_genomics_monthly_amount',
            'mpsa_true_genomics_fee_type',
            'mpsa_true_genomics_test_kits',
            'mpsa_diabetes_mgmt_eng_member',
            'mpsa_diabetes_mgmt_pmpm',
            'mpsa_weight_management',
            'mpsa_weight_management_pmpm',
            'mpsa_id_cards_fee',
            'mpsa_initial_id_card_fee',
            'mpsa_mailings',
            'mpsa_creditable_coverage',
            'mpsa_pos_rebates',
            'mpsa_welcome_packet',
            'mpsa_misc',
            'mpsa_misc2'
          ]}
          bulkActionButtons={false}
        >
          {/* Default fields */}
          <TextField source="recon_billing_client_number" label="Recon Billing Client Number" />
          <TextField source="recon_gp_customer_id" label="Recon GP Customer ID" />
          <TextField source="recon_client_name_dst" label="Recon Client Name DST" />
          <TextField source="recon_tpa" label="Recon TPA" />
          <TextField source="recon_cycle" label="Recon Cycle" />
          <TextField source="recon_breakouts" label="Recon Breakouts" />
          <TextField source="recon_gp_descriptions" label="Recon GP Description" />
          <TextField source="non_phi" label="Non PHI" />
          <FunctionField
            source="recon_admin_fee"
            label="Recon Admin Fee"
            render={record => formatMonetaryValue(record.recon_admin_fee)}
          />
          <FunctionField
            source="mpsa_true_assist"
            label="True Assist %"
            render={record => formatPercentage(record.mpsa_true_assist)}
          />
          {/* Omitted fields */}
          <FunctionField
            source="recon_narrow_fee"
            label="Recon Narrow Fee"
            render={record => formatMonetaryValue(record.recon_narrow_fee)}
          />
          <FunctionField
            source="recon_kinetiq_health_fee"
            label="Recon Kinetiq Health Fee"
            render={record => formatMonetaryValue(record.recon_kinetiq_health_fee)}
          />
          <FunctionField
            source="recon_turveris_fee"
            label="Recon Turveris Fee"
            render={record => formatMonetaryValue(record.recon_turveris_fee)}
          />
          <FunctionField
            source="recon_tpa_or_broker_fee"
            label="Recon TPA or Broker Fee"
            render={record => formatMonetaryValue(record.recon_tpa_or_broker_fee)}
          />
          <FunctionField
            source="recon_pgx_fee"
            label="Recon PGX Fee"
            render={record => formatMonetaryValue(record.recon_pgx_fee)}
          />
          <FunctionField
            source="recon_total_fees"
            label="Recon Total Fees"
            render={record => formatMonetaryValue(record.recon_total_fees)}
          />
          <TextField source="recon_client_name_gp" label="Recon Client Name GP" />
          <TextField source="recon_utilizes_pos" label="Recon Utilizes POS" />
          <TextField source="recon_step_therapy" label="Recon Step Therapy" />
          <TextField source="mpsa_client_number" label="MPSA Client Number" />
          <TextField source="mpsa_client_name" label="MPSA Client Name" />
          <TextField source="mpsa_contract_type" label="MPSA Contract Type" />
          <TextField source="mpsa_contract_effective_date" label="MPSA Contract Effective Date" />
          <TextField source="mpsa_fully_executed" label="MPSA Fully Executed" />
          <TextField source="mpsa_payment_terms" label="MPSA Payment Terms" />
          <TextField source="mpsa_admin_fee_type" label="MPSA Admin Fee Type" />
          <FunctionField
            source="mpsa_admin_fee_minimum"
            label="MPSA Admin Fee Minimum"
            render={record => formatMonetaryValue(record.mpsa_admin_fee_minimum)}
          />
          <FunctionField
            source="mpsa_admin_fee_amount"
            label="MPSA Admin Fee Amount"
            render={record => formatMonetaryValue(record.mpsa_admin_fee_amount)}
          />
          <FunctionField
            source="mpsa_narrow_fee"
            label="MPSA Narrow Fee"
            render={record => formatMonetaryValue(record.mpsa_narrow_fee)}
          />
          <FunctionField
            source="mpsa_kinetiq_fee"
            label="MPSA Kinetiq Fee"
            render={record => formatMonetaryValue(record.mpsa_kinetiq_fee)}
          />
          <FunctionField
            source="mpsa_turveris_fee"
            label="MPSA Turveris Fee"
            render={record => formatMonetaryValue(record.mpsa_turveris_fee)}
          />
          <FunctionField
            source="mpsa_tpa_or_broker_fee"
            label="MPSA TPA or Broker Fee"
            render={record => formatMonetaryValue(record.mpsa_tpa_or_broker_fee)}
          />
          <FunctionField
            source="mpsa_pgx_fee"
            label="MPSA PGX Fee"
            render={record => formatMonetaryValue(record.mpsa_pgx_fee)}
          />
          <FunctionField
            source="mpsa_total_fee"
            label="MPSA Total Fee"
            render={record => formatMonetaryValue(record.mpsa_total_fee)}
          />
          <FunctionField
            source="mpsa_misc_amount"
            label="MPSA Misc Amount"
            render={record => formatMonetaryValue(record.mpsa_misc_amount)}
          />
          <TextField source="mpsa_misc_fee_description" label="MPSA Misc Fee Description" />
          <FunctionField
            source="mpsa_pa_clinical_fee"
            label="MPSA PA Clinical Fee"
            render={record => formatMonetaryValue(record.mpsa_pa_clinical_fee)}
          />
          <FunctionField
            source="mpsa_pa_specialty_fee"
            label="MPSA PA Specialty Fee"
            render={record => formatMonetaryValue(record.mpsa_pa_specialty_fee)}
          />
          <FunctionField
            source="mpsa_pa_standard_fee"
            label="MPSA PA Standard Fee"
            render={record => formatMonetaryValue(record.mpsa_pa_standard_fee)}
          />
          <FunctionField
            source="mpsa_pa_appeal1_fee"
            label="MPSA PA Appeal1 Fee"
            render={record => formatMonetaryValue(record.mpsa_pa_appeal1_fee)}
          />
          <FunctionField
            source="mpsa_pa_appeal2_fee"
            label="MPSA PA Appeal2 Fee"
            render={record => formatMonetaryValue(record.mpsa_pa_appeal2_fee)}
          />
          <TextField source="mpsa_total_access_pmpm_bundle" label="MPSA Total Access PMPM Bundle" />
          <TextField source="mpsa_full_clinical_pmpm_bundle" label="MPSA Full Clinical PMPM Bundle" />
          <TextField source="mpsa_limited_clinical_pmpm_bundle" label="MPSA Limited Clinical PMPM Bundle" />
          <FunctionField
            source="mpsa_true_advocate_percentage"
            label="MPSA True Advocate %"
            render={record => formatPercentage(record.mpsa_true_advocate_percentage)}
          />
          <TextField source="mpsa_true_advocate_max_amount" label="MPSA True Advocate Max Amount" />
          <TextField source="mpsa_true_advocate_day_supply_cap" label="MPSA True Advocate Day Supply Cap" />
          <TextField source="mpsa_true_advocate_billing_method" label="MPSA True Advocate Billing Method" />
          <TextField source="mpsa_true_advocate_pmpm" label="MPSA True Advocate PMPM" />
          <FunctionField
            source="mpsa_true_codes_percentage"
            label="MPSA True Codes %"
            render={record => formatPercentage(record.mpsa_true_codes_percentage)}
          />
          <TextField source="mpsa_true_codes_max_amount" label="MPSA True Codes Max Amount" />
          <TextField source="mpsa_true_codes_day_supply_cap" label="MPSA True Codes Day Supply Cap" />
          <TextField source="mpsa_true_codes_billing_method" label="MPSA True Codes Billing Method" />
          <TextField source="mpsa_true_codes_pmpm" label="MPSA True Codes PMPM" />
          <TextField source="mpsa_true_genomics_monthly_amount" label="MPSA True Genomics Monthly Amount" />
          <TextField source="mpsa_true_genomics_fee_type" label="MPSA True Genomics Fee Type" />
          <TextField source="mpsa_true_genomics_test_kits" label="MPSA True Genomics Test Kits" />
          <TextField source="mpsa_diabetes_mgmt_eng_member" label="MPSA Diabetes Mgmt Eng Member" />
          <TextField source="mpsa_diabetes_mgmt_pmpm" label="MPSA Diabetes Mgmt PMPM" />
          <TextField source="mpsa_weight_management" label="MPSA Weight Management" />
          <TextField source="mpsa_weight_management_pmpm" label="MPSA Weight Management PMPM" />
          <TextField source="mpsa_id_cards_fee" label="MPSA ID Cards Fee" />
          <TextField source="mpsa_initial_id_card_fee" label="MPSA Initial ID Card Fee" />
          <TextField source="mpsa_mailings" label="MPSA Mailings" />
          <TextField source="mpsa_creditable_coverage" label="MPSA Creditable Coverage" />
          <TextField source="mpsa_pos_rebates" label="MPSA POS Rebates" />
          <TextField source="mpsa_welcome_packet" label="MPSA Welcome Packet" />
          <TextField source="mpsa_misc" label="MPSA Misc" />
          <TextField source="mpsa_misc2" label="MPSA Misc2" />
          <TextField source="recon_client_number_dst" label="Recon Client Number DST" />
          <FunctionField
            label="Last Modified Date"
            render={(record: any) => formatTimestamp(record.updated_date)}
          />
        </DatagridConfigurable>
      </List>
    </>
  )
};

export default BillingGuidelines;
